import { collection, doc, getDocs, onSnapshot, query, where, writeBatch } from 'firebase/firestore'
import { getDbOrThrow } from '@/lib/firebase'
import { mapAttributeValue } from '@/lib/mappers'
import type { AttributeType, MaterialAttributeValue } from '@/lib/types'

/**
 * Live values of EVERY material (the whole `materialAttrsValues` collection; readable by any signed-in
 * user). One document per (material, attribute), so this grows with the catalog — it is used only by
 * the consistency checks page, while it is open.
 */
export function subscribeToAllAttributeValues(
  onData: (items: MaterialAttributeValue[]) => void,
  onError: (error: Error) => void,
): () => void {
  return onSnapshot(
    collection(getDbOrThrow(), 'materialAttrsValues'),
    (snapshot) =>
      onData(
        snapshot.docs
          .map((item) => mapAttributeValue(item.id, item.data()))
          .filter((item): item is MaterialAttributeValue => item !== null),
      ),
    onError,
  )
}

/**
 * Per-material attribute values (`materialAttrsValues`): one document per (material, attribute).
 *
 * The documents denormalise the category and attribute identity (ids + name + code) so calculation
 * rules and reports can resolve a value by `attributeCode` without extra lookups — the console must
 * write the same shape, otherwise the rule engine stops finding values.
 *
 * Rules allow a write by the material's owner or by an admin.
 */
export type AttributeValueDraft = {
  categoryId: string
  categoryName: string
  attributeId: string
  attributeCode: string
  attributeName: string
  value: string
  type: AttributeType
}

export async function getAttributeValues(materialId: string): Promise<MaterialAttributeValue[]> {
  const snapshot = await getDocs(
    query(collection(getDbOrThrow(), 'materialAttrsValues'), where('materialId', '==', materialId)),
  )
  return snapshot.docs
    .map((item) => mapAttributeValue(item.id, item.data()))
    .filter((item): item is MaterialAttributeValue => item !== null)
}

/**
 * Replaces the whole value set of a material in ONE atomic batch: the existing documents are deleted
 * and the new ones written with fresh auto-ids, so a delete and a set never target the same document
 * (the same approach as `FirestoreMaterialAttributeValueRepository.replaceValues`).
 *
 * An empty list therefore means "clear all values", which is what happens when a material's category
 * is changed or cleared.
 */
export async function replaceAttributeValues(
  materialId: string,
  values: AttributeValueDraft[],
): Promise<void> {
  const db = getDbOrThrow()
  const existing = await getDocs(
    query(collection(db, 'materialAttrsValues'), where('materialId', '==', materialId)),
  )
  if (existing.empty && values.length === 0) return

  const batch = writeBatch(db)
  for (const item of existing.docs) batch.delete(item.ref)
  for (const value of values) {
    batch.set(doc(collection(db, 'materialAttrsValues')), { materialId, ...value })
  }
  await batch.commit()
}

/** Used when a material is deleted, so no orphan value documents are left behind. */
export function deleteAttributeValues(materialId: string): Promise<void> {
  return replaceAttributeValues(materialId, [])
}
